import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Lock, Upload, Download, LogOut, LayoutDashboard, FileText, ShieldCheck, AlertTriangle } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { API } from "../lib/api";

function authHeaders() {
  const t = localStorage.getItem("dh_candidate_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

// ---------- Intake form (Screenshot 3) ----------
function IntakeForm({ initialName, initialEmail, onDone }) {
  const [tz, setTz] = useState("America/Chicago");
  useEffect(() => {
    try { setTz(Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Chicago"); } catch { /* keep default */ }
  }, []);
  const [form, setForm] = useState({
    full_name: initialName || "",
    phone: "",
    location: "",
    timezone: "",
    education_level: "High School",
    employment_status: "Unemployed",
    payroll_experience: "None",
    daily_availability: "",
    has_computer: true,
    has_internet: true,
    motivation: "",
    consent_video_verify: false,
    consent_onboarding: false,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => { setForm((f) => ({ ...f, timezone: f.timezone || tz })); }, [tz]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_video_verify) { toast.error("Please consent to the video/facial verification step."); return; }
    if (!form.consent_onboarding) { toast.error("Please agree to the onboarding terms."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/enroll/me/intake`, form, { headers: authHeaders() });
      toast.success("Intake submitted!");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit intake.");
    } finally { setBusy(false); }
  };

  const inputCls = "mt-2 bg-[#FAF7EE] border-[#0F2A1F]/15 focus-visible:ring-[#0F2A1F]";
  const selectCls = "mt-2 w-full rounded-md bg-[#FAF7EE] border border-[#0F2A1F]/15 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2A1F]";

  return (
    <div className="max-w-4xl mx-auto" data-testid="intake-form-wrapper">
      <h1 className="text-3xl md:text-4xl font-bold text-[#0F2A1F]">Tell us about yourself</h1>
      <p className="mt-3 text-[#0F2A1F]/65 max-w-2xl">
        This is your candidate intake form. Once submitted, your status moves to <strong className="text-[#0F2A1F]">Form Submitted</strong> and you'll see the 14-day timeline.
      </p>
      <form onSubmit={submit} className="mt-8 rounded-xl bg-white p-7 md:p-9 ring-1 ring-[#0F2A1F]/10 space-y-6" data-testid="intake-form">
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label className="text-[#0F2A1F]">Full name</Label>
            <Input data-testid="intake-fullname" className={inputCls} value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
          </div>
          <div>
            <Label className="text-[#0F2A1F]">Phone / WhatsApp</Label>
            <Input data-testid="intake-phone" className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div>
            <Label className="text-[#0F2A1F]">Location (City, Country)</Label>
            <Input data-testid="intake-location" className={inputCls} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
          </div>
          <div>
            <Label className="text-[#0F2A1F]">Time zone (auto-detected — edit if wrong)</Label>
            <Input data-testid="intake-tz" className={inputCls} value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} />
          </div>
          <div>
            <Label className="text-[#0F2A1F]">Education level</Label>
            <select data-testid="intake-education" className={selectCls} value={form.education_level} onChange={(e) => setForm({ ...form, education_level: e.target.value })}>
              <option>High School</option><option>Associate's</option><option>Bachelor's</option><option>Master's</option><option>Other</option>
            </select>
          </div>
          <div>
            <Label className="text-[#0F2A1F]">Employment status</Label>
            <select data-testid="intake-employment" className={selectCls} value={form.employment_status} onChange={(e) => setForm({ ...form, employment_status: e.target.value })}>
              <option>Unemployed</option><option>Part-time</option><option>Full-time</option><option>Student</option><option>Other</option>
            </select>
          </div>
          <div>
            <Label className="text-[#0F2A1F]">Payroll / admin experience</Label>
            <select data-testid="intake-experience" className={selectCls} value={form.payroll_experience} onChange={(e) => setForm({ ...form, payroll_experience: e.target.value })}>
              <option>None</option><option>Less than 1 year</option><option>1-2 years</option><option>2+ years</option>
            </select>
          </div>
          <div>
            <Label className="text-[#0F2A1F]">Daily availability</Label>
            <Input data-testid="intake-availability" className={inputCls} placeholder="e.g., 3 hours, mornings" value={form.daily_availability} onChange={(e) => setForm({ ...form, daily_availability: e.target.value })} />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <label className="flex items-center gap-3 rounded-md border border-[#0F2A1F]/15 bg-[#FAF7EE]/40 px-4 py-3 cursor-pointer" data-testid="intake-computer-label">
            <input type="checkbox" checked={form.has_computer} onChange={(e) => setForm({ ...form, has_computer: e.target.checked })} data-testid="intake-computer" />
            <span className="text-sm text-[#0F2A1F]">I have a working computer / laptop</span>
          </label>
          <label className="flex items-center gap-3 rounded-md border border-[#0F2A1F]/15 bg-[#FAF7EE]/40 px-4 py-3 cursor-pointer" data-testid="intake-internet-label">
            <input type="checkbox" checked={form.has_internet} onChange={(e) => setForm({ ...form, has_internet: e.target.checked })} data-testid="intake-internet" />
            <span className="text-sm text-[#0F2A1F]">I have stable internet access</span>
          </label>
        </div>

        <div>
          <Label className="text-[#0F2A1F]">Why do you want to join DirectHire?</Label>
          <Textarea data-testid="intake-motivation" rows={4} className={inputCls} value={form.motivation} onChange={(e) => setForm({ ...form, motivation: e.target.value })} />
        </div>

        <div className="rounded-md bg-[#F8DCDB]/50 border border-[#0F2A1F]/10 p-5">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#0F2A1F]/55">Documents</div>
          <p className="mt-2 text-sm text-[#0F2A1F]/70">
            Your CV and government-issued ID are collected on the DirectHire main site — not here. Use the links below if you haven't uploaded them yet.
          </p>
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <div className="flex items-center justify-between rounded-md bg-white p-3 ring-1 ring-[#0F2A1F]/10">
              <div className="flex items-center gap-3 text-sm">
                <FileText className="h-4 w-4 text-[#0F2A1F]/55" />
                <div>
                  <div className="font-medium text-[#0F2A1F]">CV / Resume</div>
                  <div className="text-xs text-[#0F2A1F]/55">Optional — upload after intake</div>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between rounded-md bg-white p-3 ring-1 ring-[#0F2A1F]/10">
              <div className="flex items-center gap-3 text-sm">
                <ShieldCheck className="h-4 w-4 text-[#0F2A1F]/55" />
                <div>
                  <div className="font-medium text-[#0F2A1F]">Government-issued ID</div>
                  <div className="text-xs text-[#0F2A1F]/55">Required on Day 0</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <label className="flex items-start gap-3 rounded-md bg-[#F8DCDB]/50 border border-[#0F2A1F]/10 p-4 cursor-pointer text-sm" data-testid="intake-consent-video-label">
          <input type="checkbox" className="mt-0.5" checked={form.consent_video_verify} onChange={(e) => setForm({ ...form, consent_video_verify: e.target.checked })} data-testid="intake-consent-video" />
          <span className="text-[#0F2A1F]/85">
            I understand that DirectHire may invite me to a short <strong>video or facial verification call</strong> within the first few days of the program to confirm my identity matches my uploaded ID. I consent to this verification step if requested.
          </span>
        </label>

        <label className="flex items-start gap-3 text-sm cursor-pointer" data-testid="intake-consent-onboarding-label">
          <input type="checkbox" className="mt-0.5" checked={form.consent_onboarding} onChange={(e) => setForm({ ...form, consent_onboarding: e.target.checked })} data-testid="intake-consent-onboarding" />
          <span className="text-[#0F2A1F]/85">
            I agree to receive onboarding messages from DirectHire and understand the 14-day program is part of the candidate evaluation process.
          </span>
        </label>

        <button type="submit" disabled={busy} className="rounded-md bg-[#0F2A1F] text-white px-7 py-3 text-sm font-medium hover:bg-[#163b2c] disabled:opacity-60" data-testid="intake-submit-btn">
          {busy ? "Submitting…" : "Submit intake form"}
        </button>
      </form>
    </div>
  );
}

// ---------- Document Upload Card ----------
function DocsCard({ idUploaded, cvUploaded, onChange }) {
  const idRef = useRef(null);
  const cvRef = useRef(null);
  const [busy, setBusy] = useState(null);

  const upload = async (kind, file) => {
    if (!file) return;
    setBusy(kind);
    try {
      const fd = new FormData();
      fd.append(kind === "id" ? "id_file" : "cv_file", file);
      const ep = kind === "id" ? "upload-id" : "upload-cv";
      await axios.post(`${API}/enroll/me/${ep}`, fd, { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } });
      toast.success(`${kind === "id" ? "ID" : "CV"} uploaded!`);
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(null); }
  };

  return (
    <div className="rounded-xl bg-white p-6 ring-1 ring-[#0F2A1F]/10" data-testid="docs-card">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#0F2A1F]/55">Your documents</div>
          <h3 className="mt-1 text-xl font-bold text-[#0F2A1F]">Verification documents</h3>
        </div>
        {!idUploaded && (
          <span className="rounded-full bg-[#F8DCDB] text-[#8b3a3a] text-[11px] font-medium px-2.5 py-1 inline-flex items-center gap-1" data-testid="id-required-badge">
            <AlertTriangle className="h-3 w-3" /> ID required
          </span>
        )}
      </div>
      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        {/* ID */}
        <div className={`rounded-lg p-4 flex items-center justify-between gap-3 ${idUploaded ? "bg-[#FAF7EE] ring-1 ring-[#0F2A1F]/10" : "bg-[#F8DCDB]/40 ring-1 ring-[#8b3a3a]/30"}`}>
          <div className="flex items-center gap-3 text-sm">
            <FileText className={`h-5 w-5 ${idUploaded ? "text-[#0F2A1F]/55" : "text-[#8b3a3a]"}`} />
            <div>
              <div className="font-medium text-[#0F2A1F]">Government-issued ID</div>
              <div className="text-xs text-[#0F2A1F]/55">
                {idUploaded ? "Uploaded ✓" : "Driver's license / national ID / passport · required"}
              </div>
            </div>
          </div>
          <input ref={idRef} type="file" accept="image/*,application/pdf" className="hidden" data-testid="id-file-input" onChange={(e) => upload("id", e.target.files?.[0])} />
          <button type="button" onClick={() => idRef.current?.click()} className="rounded-md bg-white ring-1 ring-[#0F2A1F]/15 px-3 py-2 text-xs font-medium text-[#0F2A1F] inline-flex items-center gap-1.5 hover:ring-[#0F2A1F]/40" data-testid="upload-id-btn">
            <Upload className="h-3.5 w-3.5" /> {busy === "id" ? "…" : (idUploaded ? "Replace" : "Upload")}
          </button>
        </div>
        {/* CV */}
        <div className="rounded-lg p-4 flex items-center justify-between gap-3 bg-[#FAF7EE] ring-1 ring-[#0F2A1F]/10">
          <div className="flex items-center gap-3 text-sm">
            <FileText className="h-5 w-5 text-[#0F2A1F]/55" />
            <div>
              <div className="font-medium text-[#0F2A1F]">CV / Resume</div>
              <div className="text-xs text-[#0F2A1F]/55">
                {cvUploaded ? "Uploaded ✓" : "Optional but recommended"}
              </div>
            </div>
          </div>
          <input ref={cvRef} type="file" accept="application/pdf,.doc,.docx" className="hidden" data-testid="cv-file-input" onChange={(e) => upload("cv", e.target.files?.[0])} />
          <button type="button" onClick={() => cvRef.current?.click()} className="rounded-md bg-white ring-1 ring-[#0F2A1F]/15 px-3 py-2 text-xs font-medium text-[#0F2A1F] inline-flex items-center gap-1.5 hover:ring-[#0F2A1F]/40" data-testid="upload-cv-btn">
            <Upload className="h-3.5 w-3.5" /> {busy === "cv" ? "…" : (cvUploaded ? "Replace" : "Upload")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Day Detail (right column) ----------
function DayDetail({ day, totalDays, onSubmit, submitting }) {
  const [answer, setAnswer] = useState("");
  useEffect(() => { setAnswer(""); }, [day?.day]);
  if (!day) return null;

  // Synthetic Day 0
  if (day.day === 0) {
    return (
      <div className="rounded-xl bg-white p-7 ring-1 ring-[#0F2A1F]/10" data-testid="day0-detail">
        <div className="text-[10px] tracking-[0.25em] uppercase text-[#0F2A1F]/55">Day 0</div>
        <h2 className="mt-2 text-3xl font-bold text-[#0F2A1F]">Welcome to DirectHire</h2>
        <p className="mt-4 text-[#0F2A1F]/75 leading-relaxed">
          Your application is received. Before Day 1 unlocks, please make sure your <strong>Government-issued ID</strong> is uploaded above. CV is optional but recommended.
        </p>
        <p className="mt-3 text-sm text-[#0F2A1F]/55">Day 1 unlocks 24 hours after enrollment.</p>
      </div>
    );
  }

  if (!day.unlocked) {
    return (
      <div className="rounded-xl bg-white p-12 text-center ring-1 ring-[#0F2A1F]/10" data-testid="day-locked-detail">
        <Lock className="h-10 w-10 text-[#0F2A1F]/30 mx-auto" />
        <h2 className="mt-4 text-2xl font-bold text-[#0F2A1F]">Day {day.day} unlocks soon</h2>
        <p className="mt-2 text-sm text-[#0F2A1F]/60">Each question unlocks 24 hours after the previous one.</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-white p-7 ring-1 ring-[#0F2A1F]/10" data-testid={`day-detail-${day.day}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-[10px] tracking-[0.25em] uppercase text-[#1f5a3f]">Day {day.day} of {totalDays}</span>
        {day.answered && <span className="inline-flex items-center gap-1 rounded-full bg-[#1f5a3f]/10 text-[#1f5a3f] px-2.5 py-0.5 text-xs font-semibold"><CheckCircle2 className="h-3 w-3" /> Submitted</span>}
      </div>
      <h2 className="mt-3 text-3xl font-bold text-[#0F2A1F] leading-tight">{day.title}</h2>
      <p className="mt-4 text-base text-[#0F2A1F]/75 leading-relaxed">{day.question}</p>
      {day.hint && (
        <div className="mt-5 rounded-md bg-[#FAF7EE] ring-1 ring-[#0F2A1F]/10 p-4 text-sm text-[#0F2A1F]/70" data-testid="day-hint">
          <strong className="text-[#0F2A1F]">Hint:</strong> {day.hint}
        </div>
      )}
      {day.answered ? (
        <div className="mt-6" data-testid="day-answer-display">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#0F2A1F]/55">Your answer</div>
          <div className="mt-2 rounded-md bg-[#FAF7EE]/60 ring-1 ring-[#0F2A1F]/10 p-4 text-sm text-[#0F2A1F]/80 leading-relaxed whitespace-pre-wrap">{day.answer_text}</div>
        </div>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(day.day, answer); }} className="mt-6" data-testid="day-answer-form">
          <Textarea rows={8} value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="Take your time. Be specific and use your own voice." className="bg-[#FAF7EE]/40 border-[#0F2A1F]/15 focus-visible:ring-[#0F2A1F]" data-testid="day-answer-textarea" />
          <div className="mt-2 flex items-center justify-between text-xs text-[#0F2A1F]/55">
            <span>{answer.trim().length} characters · 20+ minimum</span>
          </div>
          <button type="submit" disabled={submitting || answer.trim().length < 20} className="mt-4 w-full rounded-md bg-[#0F2A1F] text-white py-3 text-sm font-medium hover:bg-[#163b2c] disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="day-submit-btn">
            {submitting ? "Submitting…" : (<>Submit Day {day.day} <ArrowRight className="h-4 w-4" /></>)}
          </button>
        </form>
      )}
    </div>
  );
}

// ---------- Decision Letter (Screenshot 5) ----------
function DecisionLetterCard({ decision, downloadCertHref, prcId }) {
  if (!decision) return null;
  const badgeColor = decision.status === "accepted" ? "bg-[#1f5a3f] text-white" : decision.status === "hold" ? "bg-[#D4AF37] text-[#3b2c0a]" : "bg-[#8b3a3a] text-white";
  return (
    <div className="mt-6 rounded-xl bg-white p-7 ring-1 ring-[#0F2A1F]/10" data-testid="decision-letter-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-xl font-bold text-[#0F2A1F] inline-flex items-center gap-2"><FileText className="h-5 w-5" /> Your decision letter</h3>
        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${badgeColor}`} data-testid="decision-status-badge">{decision.title || decision.status}</span>
      </div>
      <div className="mt-5 whitespace-pre-wrap font-mono text-xs leading-relaxed text-[#0F2A1F]/85 max-h-[420px] overflow-auto" data-testid="decision-letter-text">{decision.letter}</div>
      {prcId && (
        <div className="mt-6 pt-5 border-t border-[#0F2A1F]/10 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#0F2A1F]/55">Your payroll readiness certificate</div>
            <div className="mt-1 font-mono text-[#0F2A1F] font-semibold">{prcId}</div>
          </div>
          <div className="flex gap-2">
            <a href={downloadCertHref} target="_blank" rel="noopener noreferrer" className="rounded-md bg-[#0F2A1F] text-white px-4 py-2 text-sm font-medium hover:bg-[#163b2c] inline-flex items-center gap-1.5" data-testid="download-cert-btn">
              <Download className="h-4 w-4" /> Download PRC (PDF)
            </a>
            <Link to={`/verify/${prcId}`} target="_blank" className="rounded-md bg-white ring-1 ring-[#0F2A1F]/20 px-4 py-2 text-sm font-medium text-[#0F2A1F] hover:ring-[#0F2A1F]/40" data-testid="verify-page-btn">Verify page</Link>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Main Portal Page ----------
export default function EnrollPortal() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeDay, setActiveDay] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [certHref, setCertHref] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/enroll/me/portal`, { headers: authHeaders() });
      setData(res.data);
      // Default day:
      // If intake not done → no preview
      // Else if decision exists → Day total (decision page)
      // Else first unanswered unlocked, or current
      if (res.data.intake_done) {
        if (res.data.decision) setActiveDay(res.data.total_days);
        else {
          const firstUnanswered = res.data.days.find((d) => d.unlocked && !d.answered);
          setActiveDay(firstUnanswered?.day || (res.data.id_uploaded ? res.data.current_day : 0));
        }
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("dh_candidate_token");
        nav("/enroll/login");
        return;
      }
      toast.error("Couldn't load your portal.");
    } finally { setLoading(false); }
  }, [nav]);

  useEffect(() => {
    const t = localStorage.getItem("dh_candidate_token");
    if (!t) { nav("/enroll/login"); return; }
    load();
  }, [nav, load]);

  // Build cert download URL once data loaded (need to fetch as blob since it's auth'd)
  useEffect(() => {
    if (data?.prc_id) {
      // Use a fetch wrapper to load PDF with auth & return an object URL
      const fetchCert = async () => {
        try {
          const r = await fetch(`${API}/enroll/me/certificate.pdf`, { headers: authHeaders() });
          if (!r.ok) return;
          const b = await r.blob();
          setCertHref(URL.createObjectURL(b));
        } catch { /* ignore */ }
      };
      fetchCert();
    }
  }, [data?.prc_id]);

  const submitAnswer = async (day, answer) => {
    if (answer.trim().length < 20) { toast.error("Please write at least 20 characters."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enroll/me/answer`, { day, answer }, { headers: authHeaders() });
      toast.success(`Day ${day} submitted!`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit answer.");
    } finally { setSubmitting(false); }
  };

  const logout = () => {
    localStorage.removeItem("dh_candidate_token");
    localStorage.removeItem("dh_candidate_email");
    nav("/enroll/login");
  };

  if (loading) return <div className="min-h-screen bg-[#FAF7EE] flex items-center justify-center text-[#0F2A1F]/60">Loading your portal…</div>;
  if (!data) return <div className="min-h-screen bg-[#FAF7EE] flex items-center justify-center text-[#0F2A1F]/60">No portal data.</div>;

  // Build timeline w/ synthetic Day 0
  const days = [{ day: 0, title: "Welcome to DirectHire", unlocked: true, answered: data.intake_done, isWelcome: true }, ...data.days];
  const dayObj = days.find((d) => d.day === activeDay) || days[0];

  // Pre-intake: show only intake form
  if (!data.intake_done) {
    return (
      <PortalShell name={data.full_name} onLogout={logout}>
        <IntakeForm initialName={data.full_name} initialEmail={data.email} onDone={load} />
      </PortalShell>
    );
  }

  const todaysFocus = data.decision
    ? { title: "Decision Day", sub: "Outcome and next steps", target: data.total_days, label: `View Day ${data.total_days}` }
    : !data.id_uploaded
      ? { title: "Welcome to DirectHire", sub: "Application received — let's get you set up", target: 0, label: "Open Day 0" }
      : { title: data.days.find((d) => d.day === data.current_day)?.title || "Today's task", sub: `Day ${data.current_day} of ${data.total_days}`, target: data.current_day, label: `Open Day ${data.current_day}` };

  const progressPct = Math.round((data.days_completed / data.total_days) * 100);
  const statusBadge = data.decision
    ? <span className="rounded-full bg-[#1f5a3f] text-white text-xs px-3 py-1 font-medium" data-testid="status-badge">{data.decision.title || data.decision.status}</span>
    : <span className="rounded-full bg-[#FAF7EE] ring-1 ring-[#0F2A1F]/15 text-[#0F2A1F] text-xs px-3 py-1 font-medium" data-testid="status-badge">Form Submitted</span>;

  return (
    <PortalShell name={data.full_name} onLogout={logout}>
      <div className="max-w-7xl mx-auto" data-testid="portal-main">
        {/* Documents card */}
        <DocsCard idUploaded={data.id_uploaded} cvUploaded={data.cv_uploaded} onChange={load} />

        {/* Welcome + Today's Focus */}
        <div className="mt-6 grid md:grid-cols-3 gap-5">
          <div className="md:col-span-2 rounded-xl bg-white p-6 ring-1 ring-[#0F2A1F]/10">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#0F2A1F]/55">Welcome back</div>
                <h2 className="mt-2 text-3xl font-bold text-[#0F2A1F]">Hello, {data.full_name}</h2>
                <p className="mt-2 text-sm text-[#0F2A1F]/65">You're {progressPct}% through the {data.track} program. Keep your daily momentum.</p>
              </div>
              {statusBadge}
            </div>
            <div className="mt-5">
              <div className="flex items-center justify-between text-xs text-[#0F2A1F]/55"><span>{data.days_completed} of {data.total_days} days</span><span>{progressPct}%</span></div>
              <div className="mt-1.5 h-2 rounded-full bg-[#0F2A1F]/10 overflow-hidden">
                <div className="h-full bg-[#0F2A1F] transition-all duration-500" style={{ width: `${progressPct}%` }} />
              </div>
            </div>
          </div>
          <div className="rounded-xl bg-white p-6 ring-1 ring-[#0F2A1F]/10" data-testid="todays-focus">
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#0F2A1F]/55">Today's focus</div>
            <h3 className="mt-2 text-xl font-bold text-[#0F2A1F]">{todaysFocus.title}</h3>
            <p className="mt-1.5 text-sm text-[#0F2A1F]/60">{todaysFocus.sub}</p>
            <button onClick={() => setActiveDay(todaysFocus.target)} className="mt-5 rounded-md bg-[#0F2A1F] text-white px-4 py-2 text-sm font-medium inline-flex items-center gap-1.5 hover:bg-[#163b2c]" data-testid="open-today-btn">
              {todaysFocus.label} <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Decision Letter (if available) */}
        <DecisionLetterCard decision={data.decision} downloadCertHref={certHref} prcId={data.prc_id} />

        {/* Timeline + Day detail */}
        <div className="mt-6 grid md:grid-cols-12 gap-5">
          {/* Timeline */}
          <aside className="md:col-span-3">
            <div className="rounded-xl bg-white p-5 ring-1 ring-[#0F2A1F]/10 sticky top-6" data-testid="timeline-sidebar">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#0F2A1F]/55">{data.track === "7-day" ? "7-Day" : "14-Day"} Timeline</div>
              <ul className="mt-4 space-y-1.5">
                {days.map((d) => {
                  const isActive = d.day === activeDay;
                  const cls = d.answered
                    ? "bg-[#1f5a3f]/10 text-[#1f5a3f]"
                    : d.unlocked
                      ? "text-[#0F2A1F] hover:bg-[#0F2A1F]/[0.04]"
                      : "text-[#0F2A1F]/35";
                  return (
                    <li key={d.day}>
                      <button type="button" disabled={!d.unlocked} onClick={() => setActiveDay(d.day)} className={`w-full text-left rounded-md px-3 py-2 text-sm transition-all ${cls} ${isActive ? "ring-2 ring-[#0F2A1F]" : ""}`} data-testid={`timeline-day-${d.day}`}>
                        <div className="flex items-center gap-2">
                          {d.answered ? <CheckCircle2 className="h-3.5 w-3.5" /> : !d.unlocked ? <Lock className="h-3.5 w-3.5" /> : <span className="h-3.5 w-3.5 rounded-full border border-current opacity-40" />}
                          <span className="font-medium">Day {d.day}</span>
                        </div>
                        {d.title && <div className="ml-5 text-xs opacity-75 truncate">{d.title}</div>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </aside>

          {/* Day Detail + Full day list */}
          <div className="md:col-span-9 space-y-4">
            <DayDetail day={dayObj} totalDays={data.total_days} onSubmit={submitAnswer} submitting={submitting} />

            {/* Compact full day list (matches right column in screenshot 4) */}
            <div className="space-y-2.5" data-testid="day-list">
              {days.map((d) => (
                <button
                  key={d.day}
                  type="button"
                  disabled={!d.unlocked}
                  onClick={() => setActiveDay(d.day)}
                  className={`w-full text-left rounded-lg bg-white p-4 ring-1 transition-all flex items-center gap-4 ${d.day === activeDay ? "ring-[#0F2A1F]" : "ring-[#0F2A1F]/10 hover:ring-[#0F2A1F]/30"} ${!d.unlocked ? "opacity-60 cursor-not-allowed" : ""}`}
                  data-testid={`day-row-${d.day}`}
                >
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center text-sm font-bold ${d.answered ? "bg-[#1f5a3f] text-white" : "bg-[#FAF7EE] text-[#0F2A1F]/70 ring-1 ring-[#0F2A1F]/10"}`}>{d.day}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#0F2A1F]/55">
                      <span>Day {d.day}</span>
                      {!d.unlocked && <span className="inline-flex items-center gap-1">· Locked <Lock className="h-3 w-3" /></span>}
                    </div>
                    <div className="mt-0.5 font-bold text-[#0F2A1F] truncate">{d.title}</div>
                    {d.question && <div className="mt-1 text-sm text-[#0F2A1F]/60 truncate">{d.question}</div>}
                  </div>
                  {d.unlocked && <span className="text-xs text-[#0F2A1F] font-medium inline-flex items-center gap-1 whitespace-nowrap">{d.answered ? "View" : "Open"} <ArrowRight className="h-3.5 w-3.5" /></span>}
                  {!d.unlocked && <Lock className="h-4 w-4 text-[#0F2A1F]/35" />}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </PortalShell>
  );
}

function PortalShell({ name, onLogout, children }) {
  return (
    <div className="min-h-screen bg-[#FAF7EE]" data-testid="portal-shell">
      <header className="bg-[#FAF7EE] border-b border-[#0F2A1F]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/enroll/portal" className="flex items-center gap-3" data-testid="portal-logo">
            <div className="h-10 w-10 rounded-md bg-[#0F2A1F] text-white flex items-center justify-center font-bold text-sm">DH</div>
            <div className="leading-tight">
              <div className="font-serif text-lg text-[#0F2A1F]">DirectHire</div>
              <div className="text-[10px] tracking-[0.25em] text-[#0F2A1F]/55 uppercase">Enrollment</div>
            </div>
          </Link>
          <nav className="flex items-center gap-6 text-sm text-[#0F2A1F]/80">
            <Link to="/career-pathway" className="hidden sm:inline hover:text-[#0F2A1F]" data-testid="portal-nav-career">Career Pathway</Link>
            <Link to="/for-candidates" className="hidden sm:inline hover:text-[#0F2A1F]" data-testid="portal-nav-grads">Graduates</Link>
            <span className="hidden md:inline-flex items-center gap-1.5 text-[#0F2A1F]" data-testid="portal-name"><LayoutDashboard className="h-4 w-4" /> Dashboard</span>
            <button onClick={onLogout} className="inline-flex items-center gap-1.5 text-[#0F2A1F] hover:underline" data-testid="portal-signout-btn"><LogOut className="h-4 w-4" /> Sign out</button>
          </nav>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">{children}</main>
    </div>
  );
}
